import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LayoutDashboard,
  Stethoscope,
  CalendarDays,
  Users,
  FileText,
  MessageSquare,
  CreditCard,
  Building2,
  Settings,
  ChevronLeft,
  ChevronDown,
  LogOut,
  UserCog,
  PhoneCall,
  UserCheck,
  FlaskConical,
  TestTubes,
  History,
  Pill,
  ClipboardList,
  Menu,
  X,
  Globe,
  TrendingUp,
  ScanLine,
  Microscope,
  Target,
  MessagesSquare,
  Inbox,
} from "lucide-react";
import { hasAnyRole, isSuperAdmin, isPureTechnician } from "../lib/access-control";
import { isModuleEnabled } from "../lib/modules";
import { IS_DEMO } from "../lib/env";
import { getDiagnosticDepartments } from "../api/api";
import { logoutThunk } from "../store/slice/users-slice";
import NotificationBell from "./NotificationBell";
import { cn } from "../lib/utils";

const NAV_GROUPS = [
  {
    label: "Care Desk",
    items: [
      { path: "/", icon: LayoutDashboard, label: "Dashboard", roles: ["caredesk", "finance", "master"] },
      { path: "/opd", icon: Stethoscope, label: "OPD Booking", roles: ["caredesk"], module: "opd" },
      { path: "/appointments", icon: CalendarDays, label: "Appointments", roles: ["caredesk"], module: "appointments" },
      { path: "/lead-inbox", icon: Inbox, label: "Lead Inbox", roles: ["caredesk"], module: "leads" },
      // Demo-only mock (dummy data) — never in a real clinic's build.
      ...(IS_DEMO
        ? [
            { path: "/leads", icon: Target, label: "Leads", roles: ["caredesk"] },
            { path: "/whatsapp", icon: MessagesSquare, label: "WhatsApp Inbox", roles: ["caredesk"] },
          ]
        : []),
      { path: "/doctor-attendance", icon: UserCheck, label: "Doctor Attendance", roles: ["caredesk"], module: "doctor_attendance" },
      { path: "/lab-orders", icon: FlaskConical, label: "Lab Orders", roles: ["caredesk"], module: "lab_orders" },
      { path: "/prescriptions", icon: FileText, label: "Prescriptions", roles: ["caredesk"], module: "prescriptions" },
      { path: "/messages", icon: MessageSquare, label: "Messages", roles: ["caredesk"], module: "messages" },
      { path: "/activity", icon: History, label: "Activity Log", roles: ["caredesk", "finance", "master"], module: "activity" },
    ],
  },
  {
    // `strict` — a doctor's own portal isn't useful to anyone without a
    // linked doctor_id, so unlike every other group here, super-admin does
    // NOT see it just by virtue of bypassing role checks.
    label: "My Practice",
    items: [
      { path: "/", icon: LayoutDashboard, label: "Dashboard", roles: ["doctor"], strict: true, module: "doctor_portal" },
      { path: "/my-appointments", icon: CalendarDays, label: "My Appointments", roles: ["doctor"], strict: true, module: "doctor_portal" },
      { path: "/my-patients", icon: Users, label: "My Patients", roles: ["doctor"], strict: true, module: "doctor_portal" },
      { path: "/my-booking", icon: Stethoscope, label: "Book a Patient", roles: ["doctor"], strict: true, module: "doctor_portal" },
    ],
  },
  {
    label: "Call Center",
    items: [
      { path: "/call-center", icon: PhoneCall, label: "Call Center", roles: ["caredesk"], requiresFeature: "click_to_call_enabled" },
    ],
  },
  {
    label: "Finance",
    items: [
      { path: "/billing", icon: CreditCard, label: "Billing & Invoices", roles: ["finance"], module: "billing" },
      { path: "/stats", icon: TrendingUp, label: "Stats", roles: ["super-admin", "admin"], module: "stats" },
    ],
  },
  {
    label: "Master",
    items: [
      { path: "/doctors", icon: Stethoscope, label: "Doctors", roles: ["master", "admin"], module: "doctors" },
      { path: "/patients", icon: Users, label: "Patients", roles: ["caredesk", "master"], module: "patients" },
      { path: "/lab-catalog", icon: TestTubes, label: "Test Catalogue", roles: ["master"], module: "lab_catalog" },
      { path: "/labs", icon: FlaskConical, label: "Partner Labs", roles: ["master"], module: "labs" },
      { path: "/diagnostic-departments", icon: Microscope, label: "Diagnostic Departments", roles: ["master"], module: "diagnostic_departments" },
      { path: "/medicines", icon: Pill, label: "Medicines", roles: ["master"], module: "medicines" },
      { path: "/clinical-terms", icon: ClipboardList, label: "Diagnoses & Symptoms", roles: ["master"], module: "clinical_terms" },
      { path: "/departments", icon: Building2, label: "Departments", roles: ["master"], module: "departments" },
      { path: "/settings", icon: Settings, label: "Settings", roles: ["master"] },
    ],
  },
  {
    label: "Admin",
    items: [{ path: "/users", icon: UserCog, label: "User Master", roles: ["super-admin"] }],
  },
  {
    // Aivexa-internal — gated on the `platform_admin` account flag, not a
    // role, so a client's own super-admin never sees this just by being a
    // super-admin within their own clinic.
    label: "Platform",
    items: [{ path: "/platform-admin", icon: Globe, label: "Platform Admin", requiresPlatformAdmin: true }],
  },
];

// Event the Diagnostic Departments master fires after a change, so the
// sidebar's per-department entries refresh without a page reload.
export const DIAGNOSTIC_DEPARTMENTS_CHANGED = "medipro:diagnostic-departments-changed";

const readCollapsedGroups = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem("sidebarCollapsedGroups") || "[]"));
  } catch {
    return new Set();
  }
};

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebarCollapsed") === "true");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(readCollapsedGroups);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.users.user);
  const clinicName = useSelector((s) => s.settings.data?.clinic?.name) || "MediPro";
  const settings = useSelector((s) => s.settings.data);
  const features = settings?.features;
  const [diagnosticDepartments, setDiagnosticDepartments] = useState([]);
  const pureTechnician = isPureTechnician(user);

  // Each diagnostic department is its own sidebar entry — a technician sees
  // only theirs (the API scopes the list), the desk sees every one.
  const showsDiagnostics =
    !user?.platform_admin && isModuleEnabled(settings, "diagnostics") &&
    (isSuperAdmin(user) || hasAnyRole(user, ["technician", "caredesk"]));
  useEffect(() => {
    if (!showsDiagnostics) return;
    const load = () =>
      getDiagnosticDepartments()
        .then((res) => setDiagnosticDepartments(res.data))
        .catch(() => setDiagnosticDepartments([]));
    load();
    window.addEventListener(DIAGNOSTIC_DEPARTMENTS_CHANGED, load);
    return () => window.removeEventListener(DIAGNOSTIC_DEPARTMENTS_CHANGED, load);
  }, [showsDiagnostics]);

  // Ctrl+D jumps straight to OPD Booking from anywhere in the dashboard —
  // Care Desk's single most common action, so it gets a global shortcut.
  // preventDefault stops the browser's own "Bookmark this page" for Ctrl+D.
  // No role check needed here: RoleMiddleware on the /opd route already
  // bounces anyone without access back to "/".
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "d") {
        e.preventDefault();
        navigate("/opd");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem("sidebarCollapsed", String(!prev));
      return !prev;
    });
  };

  const toggleGroup = (label) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      localStorage.setItem("sidebarCollapsedGroups", JSON.stringify([...next]));
      return next;
    });
  };

  // A platform_admin account is Aivexa-internal and exclusive to the
  // Platform Admin section — it never sees this clinic's own client-facing
  // nav (Dashboard, Patients, Settings, etc.), even if it also happens to
  // hold a clinic role. A regular super-admin's nav is unaffected.
  const diagnosticsGroup = {
    label: "Diagnostics",
    items: (showsDiagnostics ? diagnosticDepartments : []).map((d) => ({
      path: `/diagnostics/${d._id}`,
      icon: ScanLine,
      label: d.name,
      roles: ["technician", "caredesk"],
      module: "diagnostics",
    })),
  };
  const navGroups = [NAV_GROUPS[0], diagnosticsGroup, ...NAV_GROUPS.slice(1)];

  const visibleGroups = user?.platform_admin
    ? navGroups.filter((group) => group.items.some((item) => item.requiresPlatformAdmin))
    : navGroups.map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.requiresPlatformAdmin) return false;
          // Defaults to visible (true) until settings load, then follows the
          // per-clinic toggle — undefined/missing means the feature is on
          // (matches the schema default), only an explicit `false` hides it.
          if (item.requiresFeature && features?.[item.requiresFeature] === false) return false;
          if (!isModuleEnabled(settings, item.module)) return false;
          if (!item.roles) return true;
          if (item.strict) return hasAnyRole(user, item.roles);
          return isSuperAdmin(user) || hasAnyRole(user, item.roles);
        }),
      })).filter((group) => group.items.length > 0);

  const handleLogout = async () => {
    await dispatch(logoutThunk());
    navigate("/");
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-sidebar text-sidebar-foreground transition-transform duration-200",
          "lg:static lg:translate-x-0 lg:transition-[width]",
          collapsed ? "lg:w-20" : "lg:w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between px-4 py-5">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground font-serif font-bold">
              {clinicName[0]?.toUpperCase()}
            </span>
            {(!collapsed || mobileOpen) && (
              <span className="truncate font-serif text-lg font-semibold whitespace-nowrap">
                {clinicName}
              </span>
            )}
          </div>
          <button
            onClick={toggleCollapsed}
            className="hidden rounded-md p-1 hover:bg-white/10 lg:inline-flex"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </button>
          <button
            onClick={() => setMobileOpen(false)}
            className="rounded-md p-1 hover:bg-white/10 lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-2 pb-4">
          {visibleGroups.map((group, idx) => {
            const isActive = (item) =>
              item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
            const groupHasActive = group.items.some(isActive);
            const expanded = collapsed && !mobileOpen ? true : groupHasActive || !collapsedGroups.has(group.label);

            return (
              <div key={group.label} className={cn(idx > 0 && "mt-4 border-t border-white/10 pt-4")}>
                {(!collapsed || mobileOpen) && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.label)}
                    className="flex w-full items-center justify-between px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/50 hover:text-sidebar-foreground/80"
                  >
                    {group.label}
                    <ChevronDown className={cn("h-3 w-3 transition-transform", !expanded && "-rotate-90")} />
                  </button>
                )}
                {expanded && (
                  <div className="space-y-1">
                    {group.items.map((item) => {
                      const active = isActive(item);
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                            active ? "bg-primary text-primary-foreground" : "text-sidebar-foreground/80 hover:bg-white/10"
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          {(!collapsed || mobileOpen) && <span className="truncate">{item.label}</span>}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-3">
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-white/10"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {(!collapsed || mobileOpen) && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-border bg-card px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="rounded-md p-2 hover:bg-accent lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <p className="font-serif text-lg font-semibold">{clinicName}</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Clinic-wide alerts (bookings, delays) aren't a technician's
                concern, and the API refuses them for that role anyway. */}
            {!pureTechnician && (
              <>
                <NotificationBell />
                <span className="hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
              </>
            )}
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground">
              {initials}
            </span>
            <div className="hidden text-sm sm:block">
              <p className="font-medium leading-tight">{user?.name}</p>
              <p className="text-xs capitalize text-muted-foreground leading-tight">{user?.roles?.[0]}</p>
            </div>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}
